import { type ReactNode } from 'react'
import { Link } from 'react-router'

import { type Route } from '#app/routes/_marketing+/+types/terms-of-service.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'Terms of Service - Lydra AI' },
	{ name: 'description', content: 'Lydra AI platform terms of service' },
]

function Title({ children }: { children: ReactNode }) {
	return <div className="text-xl font-semibold">{children}</div>
}

export default function TermsOfUseRoute() {
	return (
		<div className="container mx-auto max-w-3xl py-6 md:pb-12">
			<header className="mb-6 space-y-1">
				<Title>Terms of Service</Title>
				<p className="text-muted-foreground text-sm">
					Last Updated: November 13, 2025
				</p>
			</header>

			<div className="prose flex max-w-none flex-col gap-y-6">
				<p className="text-sm leading-relaxed">
					These Terms of Service (the "Terms") form a binding agreement between
					CurigenX, Inc. DBA (Doing Business As) Lydra AI ("Lydra," "we," "us,"
					"our") and the organization or individual that creates an account or
					otherwise uses the Lydra AI pharmacovigilance platform ("Customer,"
					"you," "your"). By creating an account, accessing the services, or
					signing a business associate agreement (BAA) or order form, you agree
					to these Terms.
				</p>

				<Section title="1. Overview">
					<p>
						If you accept these Terms on behalf of a company, sponsor, contract
						research organization, pharmacovigilance vendor, or other entity,
						you represent that you have authority to bind that entity.
						Individual users accessing the services under Customer's account
						(Authorized Users) must comply with these Terms and any policies we
						publish.
					</p>
				</Section>

				<Section title="2. Services">
					<p>
						Lydra provides a software-as-a-service platform for adverse event
						intake, pharmacovigilance workflow automation, AI-assisted medical
						content generation, and regulatory dossier preparation
						(collectively, the "Services"). The Services include our web
						applications, voice and telephony channels, APIs, AI models, and
						related support. Professional services or integrations may be
						provided as described in an applicable order form, statement of
						work, or MSA (Master Service Agreement) (each, an "Order"). The
						Services are decision-support tools; they are not medical devices
						and do not independently provide diagnosis or treatment.
					</p>
				</Section>

				<Section title="3. Eligibility and Accounts">
					<List
						items={[
							'Customer must have legal capacity to enter into these Terms and any required BAA. Authorized Users must be at least 18 years old (or the age of majority in their jurisdiction).',
							'Customer is responsible for account information accuracy, for maintaining confidentiality of credentials, and for ensuring Authorized Users keep login details secure.',
							'Customer must implement least-privilege access controls, promptly deactivate departed Authorized Users, and notify Lydra of any unauthorized access or suspected compromise.',
						]}
					/>
				</Section>

				<Section title="4. Roles and Regulatory Responsibilities">
					<p>
						Pharmacovigilance obligations, regulatory submissions, and clinical
						decisions remain Customer's responsibility. Lydra provides workflow
						support and automation but does not determine case report outcomes
						or ensure compliance with 21 CFR Part 314/600, EMA GVP, or other
						jurisdictional requirements. Customer must validate all data
						produced through the Services before filing with regulators or using
						results in clinical contexts.
					</p>
					<p>
						When the Services process protected health information (PHI) on
						behalf of a HIPAA covered entity, Customer must execute a BAA with
						Lydra. Lydra will comply with the BAA and applicable HIPAA
						regulations. Customer must obtain required authorizations to
						transmit PHI and ensure disclosures follow the minimum necessary
						standard.
					</p>
				</Section>

				<Section title="5. Customer Data and Licenses">
					<p>
						"Customer Data" means all data, content, and information submitted
						to or generated within the Services by or for Customer, including
						PHI, personal data, adverse event narratives, audio recordings,
						transcripts, attachments, structured case data, usage logs, and
						configuration. Customer retains ownership of Customer Data.
					</p>
					<p>
						Customer grants Lydra and its subprocessors a worldwide,
						non-exclusive, royalty-free license to host, process, transmit,
						translate, display, and otherwise use Customer Data solely to
						provide, secure, maintain, support, and improve the Services; to
						develop anonymized or de-identified insights; and to meet legal or
						regulatory obligations. Lydra will not disclose Customer Data except
						as described in these Terms, the Privacy Policy, the BAA, or as
						instructed by Customer.
					</p>
					<p>
						Lydra may derive de-identified or aggregated data (Derived Data)
						that does not identify Customer or any individual. Lydra may use
						Derived Data to improve the Services, develop new features, or
						publish benchmarks, provided Derived Data does not include personal
						data or PHI.
					</p>
				</Section>

				<Section title="6. Acceptable Use">
					<p>
						Customer and Authorized Users must comply with the following
						Acceptable Use obligations. You will not:
					</p>
					<Subsection title="Prohibited actions">
						<List
							items={[
								'Upload or transmit unlawful, infringing, defamatory, or harmful content, including malware or code that disrupts our infrastructure.',
								'Submit PHI or regulated data without a valid BAA and required consents.',
								'Attempt to probe, scan, or test the vulnerability of the Services or Lydra infrastructure without written consent.',
								'Copy, scrape, or harvest data outside documented APIs, reverse engineer the Services, or attempt to derive source code.',
								'Use the Services to create or train competing AI or pharmacovigilance products, or benchmark Lydra for competitive purposes without consent.',
								'Generate medical advice or clinical decisions without qualified human oversight, or disregard documented workflow controls.',
								'Interfere with or disrupt networks connected to the Services, or bypass rate limits and security controls.',
								'Resell, sublicense, or share access with unauthorized third parties.',
							]}
						/>
					</Subsection>
				</Section>

				<Section title="7. AI and Automation Disclosures">
					<List
						items={[
							'Automated outputs may contain errors or bias; human review is required before using outputs for regulatory submissions, patient communications, or safety decisions.',
							'Lydra documents model versions, validation controls, and change management appropriate to the Services and their intended use, and does not provide medical advice.',
							'Customer must configure workflows to ensure qualified personnel review automated decision support.',
							"Lydra may use Customer Data to fine-tune models solely for Customer's benefit; broader use requires consent and relies on de-identified or synthetic data.",
							'Lydra provides transparency features (confidence scoring, audit trails). Customer must retain or export records needed for compliance.',
						]}
					/>
				</Section>

				<Section title="8. Third-Party Services and Subprocessors">
					<p>
						Customer may enable integrations with third-party systems. Lydra is
						not responsible for third-party services; Customer's use of them is
						governed by third-party terms. Lydra maintains a list of
						subprocessors that support the Services. Customer authorizes Lydra
						to engage subprocessors, subject to written agreements imposing data
						protection obligations no less protective than these Terms and any
						BAA.
					</p>
				</Section>

				<Section title="9. Security and Incident Response">
					<p>
						Lydra implements administrative, technical, and physical safeguards
						aligned with the HIPAA Security Rule, SOC2 controls, and good
						machine learning practices. If Lydra becomes aware of a security
						incident involving Customer Data, we will notify Customer without
						undue delay, provide information reasonably requested for
						investigation, and cooperate on mitigation. Customer must promptly
						notify Lydra of any suspected credential compromise or unauthorized
						disclosure of Customer Data.
					</p>
				</Section>

				<Section title="10. Regulatory Cooperation and Audit">
					<p>
						Lydra will reasonably cooperate with Customer's regulatory
						obligations, including providing documentation, audit trails, and
						attestations necessary for pharmacovigilance, HIPAA, or GDPR
						compliance. Upon reasonable advance notice and no more than once per
						12-month period (unless required by law), Customer may audit Lydra's
						compliance with the BAA and these Terms. Audits must occur during
						normal business hours, avoid disrupting operations, and protect
						confidentiality. Customer bears audit costs unless an audit reveals
						a material breach by Lydra.
					</p>
				</Section>

				<Section title="11. Fees and Payment">
					<p>
						Fees, billing cadence, and payment terms are described in the
						applicable Order. Unless otherwise stated, fees are invoiced
						annually in advance and due net 30 days. Late payments may incur a
						1.5% monthly finance charge (or the maximum allowed by law) plus
						collection costs. Fees are non-refundable except as expressly
						stated. Taxes are Customer's responsibility. Usage-based charges are
						billed monthly in arrears.
					</p>
				</Section>

				<Section title="12. Term, Suspension, and Termination">
					<p>
						The Subscription Term begins on the effective date of the first
						Order or account creation and continues for the period stated in the
						Order. Subscriptions renew automatically unless either party gives
						written notice of non-renewal at least 30 days before the current
						term ends. Lydra may suspend or limit access immediately if Customer
						breaches these Terms or the BAA, fails to pay undisputed fees, poses
						a security risk, or if required by law. Each party may terminate for
						cause if the other party materially breaches and does not cure
						within 30 days of written notice. Upon termination, Customer must
						stop using the Services; outstanding fees remain payable. Sections
						intended to survive termination do so.
					</p>
				</Section>

				<Section title="13. Data Export, Deletion, and Return">
					<p>
						During the Subscription Term and for 30 days after termination,
						Customer can export data via available APIs or request an export.
						After this period, Lydra may delete or anonymize Customer Data
						unless retention is required by law. If Customer requests earlier
						deletion, Lydra will delete Customer Data within a commercially
						reasonable timeframe, subject to legal retention needs.
					</p>
				</Section>

				<Section title="14. Confidentiality">
					<p>
						Each party may receive confidential information from the other. The
						recipient will use confidential information only to perform
						obligations under these Terms, protect it with at least reasonable
						care, and not disclose it except to personnel, advisors, or
						subprocessors under confidentiality obligations. Disclosures
						compelled by law are permitted with prompt notice (if legally
						allowed) and cooperation on protective measures. Exclusions apply to
						information that is public, independently developed, or lawfully
						obtained from another source.
					</p>
				</Section>

				<Section title="15. Intellectual Property and Feedback">
					<p>
						Lydra owns all rights, title, and interest in the Services,
						documentation, AI models, and underlying technology. Customer owns
						Customer Data. No additional rights transfer except as stated.
						Feedback provided by Customer or Authorized Users may be used by
						Lydra without restriction or compensation.
					</p>
				</Section>

				<Section title="16. Warranties and Disclaimers">
					<List
						items={[
							'Each party warrants that it has authority to enter these Terms and that performance will comply with applicable law.',
							"Lydra warrants that during the Subscription Term the Services will conform in all material respects to documentation and that professional services will be performed in a professional and workmanlike manner. Customer's exclusive remedy is re-performance or a pro-rata refund if Lydra cannot cure within 30 days.",
							'Except for express warranties, the Services are provided "as is" Lydra disclaims implied warranties of merchantability, fitness for a particular purpose, title, and non-infringement, and does not guarantee uninterrupted or error-free operation.',
						]}
					/>
				</Section>

				<Section title="17. Indemnification">
					<p>
						Lydra will defend Customer against third-party claims alleging that
						the Services infringe intellectual property rights, subject to
						exclusions for claims arising from Customer Data, unauthorized
						modifications, or combinations. Lydra may modify the Services,
						procure rights, or terminate impacted features with a refund.
					</p>
					<p>
						Customer will defend Lydra against third-party claims arising from
						Customer Data, use of the Services in violation of these Terms, or
						breach of the BAA or applicable law. Each party will pay damages and
						reasonable attorney fees finally awarded. The indemnified party must
						provide prompt notice, permit control of the defense, and cooperate.
					</p>
				</Section>

				<Section title="18. Limitation of Liability">
					<List
						items={[
							'Neither party is liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for lost profits, revenues, goodwill, or data, even if aware such damages were possible.',
							"Lydra's aggregate liability under these Terms will not exceed the amount paid or payable by Customer to Lydra during the 12 months preceding the event giving rise to liability.",
							"The limitations do not apply to indemnification obligations, breaches of confidentiality, violations of the Acceptable Use section, or Customer's payment obligations.",
						]}
					/>
				</Section>

				<Section title="19. Changes to the Services or Terms">
					<p>
						We may update the Services, including adding or removing features,
						provided changes do not materially reduce core functionality during
						a Subscription Term. We may update these Terms by posting a revised
						version and updating the date above. For material changes, we will
						provide at least 30 days' notice via email or in-product message.
						Continued use after the effective date constitutes acceptance. If
						Customer does not agree to a material change that materially
						degrades the Services, Customer may terminate the affected Order
						within 30 days for a pro-rata refund. Notwithstanding the foregoing,
						Lydra may implement changes required by law or necessary to address
						a material security risk immediately upon notice.
					</p>
				</Section>

				<Section title="20. Governing Law and Dispute Resolution">
					<p>
						These Terms are governed by the laws of the State of Delaware, USA,
						without regard to conflict-of-law principles. Disputes not resolved
						informally will be submitted to binding arbitration administered by
						the American Arbitration Association under its Commercial
						Arbitration Rules in San Francisco, California, before a single
						arbitrator. Judgment may be entered in any court of competent
						jurisdiction. Either party may seek injunctive or equitable relief
						to protect confidentiality or intellectual property. If Customer is
						a U.S. government entity, this section applies only to the extent
						permitted by law. The parties waive their right to a jury trial.
					</p>
				</Section>

				<Section title="21. Class Action Waiver">
					<p>
						Disputes must be brought in an individual capacity; neither party
						will participate in a class or representative action. If this waiver
						is found unenforceable, the arbitration clause is void and disputes
						will be heard in state or federal courts located in San Francisco,
						California.
					</p>
				</Section>

				<Section title="22. Government Use">
					<p>
						If Customer is a U.S. government entity or contractor, the Services
						are "commercial items" as defined at 48 C.F.R. § 2.101 and are
						provided with the same rights and restrictions applicable to
						non-government customers.
					</p>
				</Section>

				<Section title="23. Export Compliance">
					<p>
						Customer must comply with all export, re-export, and import laws,
						including the Export Administration Regulations and trade sanctions.
						Customer represents that it is not located in, and will not allow
						access from, any restricted territory or to any restricted party
						under U.S. law.
					</p>
				</Section>

				<Section title="24. Notices">
					<p>
						Notices to Lydra must be sent to{' '}
						<Link to="mailto:legal@lydra.ai" className="text-blue-600">
							legal@lydra.ai
						</Link>{' '}
						with a copy to Lydra AI, 2261 Market Street, Suite 85553, San
						Francisco, CA, 94114., United States. We may send notices to the
						email address on file for Customer's primary contact. Notices are
						deemed given when received (or, for email, when sent if no
						bounce-back is received).
					</p>
				</Section>

				<Section title="25. Miscellaneous">
					<List
						items={[
							'Order of precedence: BAA > Order > these Terms > documentation.',
							'The parties are independent contractors; these Terms do not create a partnership, joint venture, fiduciary, or employment relationship.',
							"Customer may not assign without Lydra's consent except to an affiliate or successor not considered a direct competitor and that assumes all obligations. Lydra may assign to an affiliate or in a merger or asset sale.",
							'If a provision is unenforceable, it will be modified to the minimum extent necessary; the remainder remains in effect. Failure to enforce a provision is not a waiver. Neither party is liable for failure to perform due to events beyond reasonable control, provided they mitigate and resume performance promptly.',
						]}
					/>
				</Section>

				<Section title="26. Contact">
					<p>
						For questions about these Terms or the Services, email{' '}
						<Link to="mailto:legal@lydra.ai" className="text-blue-600">
							legal@lydra.ai
						</Link>
						. For security incidents, contact{' '}
						<Link to="mailto:security@lydra.ai" className="text-blue-600">
							security@lydra.ai
						</Link>
						. For HIPAA-related inquiries, contact{' '}
						<Link to="mailto:hipaa-privacy@lydra.ai" className="text-blue-600">
							hipaa-privacy@lydra.ai
						</Link>
						.
					</p>
				</Section>
			</div>
		</div>
	)
}

function Section({
	title,
	children,
}: {
	title: ReactNode
	children: ReactNode
}) {
	return (
		<section className="space-y-2">
			<h2 className="text-lg font-semibold">{title}</h2>
			<div className="space-y-3 text-sm leading-relaxed">{children}</div>
		</section>
	)
}

function Subsection({
	title,
	children,
}: {
	title: ReactNode
	children: ReactNode
}) {
	return (
		<div className="space-y-1.5">
			<h3 className="text-base font-semibold">{title}</h3>
			<div className="space-y-3 text-sm leading-relaxed">{children}</div>
		</div>
	)
}

function List({ items }: { items: ReactNode[] }) {
	return (
		<ul className="list-inside list-disc space-y-1">
			{items.map((item, index) => (
				<li key={index}>{item}</li>
			))}
		</ul>
	)
}
